import React, {useContext, useEffect, useState} from 'react';
import {Alert, Box, Chip, CircularProgress, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Tooltip, Typography, useMediaQuery, useTheme,} from '@mui/material';
import {
    Assignment as AssignmentIcon,
    Business as BusinessIcon,
    Create as CreateIcon,
    DragIndicator as DragIndicatorIcon,
    Edit as EditIcon,
    FilterList as FilterListIcon,
    FormatListBulleted as FormatListBullet,
    Schedule as ScheduleIcon,
    ViewList as ViewListIcon,
} from '@mui/icons-material';
import {TasksService} from '../../../../services/openapi/services/TasksService';
import {ArbeitszeitService} from '../../../../services/openapi/services/ArbeitszeitService';
import type {Task} from '../../../../services/openapi/models/Task';
import type {BauvorhabenWithID} from '../../../../services/openapi/models/BauvorhabenWithID';
import {TaskStatus} from '../../../../services/openapi/models/TaskStatus';
import UserContext from '../../../../Contexts/UserContext/UserContext';
import {useSessionContext} from 'supertokens-auth-react/recipe/session';
import {PushNotificationHelper} from '../../../../utils/pushNotificationHelper';
import TaskDetail from './task-detail';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import {CollisionDetection, DndContext, DragEndEvent, DragOverlay, DragStartEvent, KeyboardSensor, PointerSensor, rectIntersection, useDroppable, useSensor, useSensors,} from '@dnd-kit/core';
import BlockIcon from '@mui/icons-material/Block';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import {SortableContext, useSortable, verticalListSortingStrategy} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';

function DroppableColumn({
                             children,
                             id,
                             isMobile,
                             isDragActive,
                             draggedFromStatus,
                         }: {
    children: React.ReactNode;
    id: string;
    isMobile: boolean;
    isDragActive: boolean;
    draggedFromStatus: string | null;
}) {
    const {setNodeRef, isOver} = useDroppable({id});

    const shouldHighlight = isDragActive && draggedFromStatus !== id;
    const isCurrentColumn = draggedFromStatus === id;

    return (
        <div
            ref={setNodeRef}
            style={{
                minHeight: isMobile ? '500px' : '700px',
                width: '100%',
                backgroundColor: isOver
                    ? 'rgba(21, 101, 192, 0.1)'
                    : shouldHighlight
                        ? 'rgba(21, 101, 192, 0.03)'
                        : isCurrentColumn
                            ? 'rgba(128, 128, 128, 0.05)'
                            : 'var(--background-default)',
                borderRadius: 'var(--border-radius)',
                transition: 'all var(--transition-normal)',
                border: isOver
                    ? '3px dashed var(--primary-main)'
                    : shouldHighlight
                        ? '2px solid rgba(21, 101, 192, 0.3)'
                        : isCurrentColumn
                            ? '1px solid rgba(128, 128, 128, 0.3)'
                            : '1px solid var(--divider)',
                padding: 'var(--spacing-sm)',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                transform: shouldHighlight ? 'scale(1.02)' : 'scale(1)',
                boxShadow: shouldHighlight
                    ? '0 4px 20px rgba(21, 101, 192, 0.15)'
                    : isOver
                        ? '0 8px 32px rgba(21, 101, 192, 0.2)'
                        : 'none',
                overflow: 'visible',
            }}>
            {/* Drop zone indicators */}
            {isOver && (
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: 'var(--primary-main)',
                        fontSize: '14px',
                        fontWeight: 'var(--font-weight-medium)',
                        textAlign: 'center',
                        pointerEvents: 'none',
                        zIndex: 1000,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    }}>
                    ↓ Hier ablegen
                </div>
            )}

            {/* Highlight indicator when dragging */}
            {shouldHighlight && !isOver && (
                <div
                    style={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        right: '8px',
                        bottom: '8px',
                        border: '2px dashed rgba(21, 101, 192, 0.4)',
                        borderRadius: 'var(--border-radius)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--primary-main)',
                        fontSize: '12px',
                        fontWeight: 'var(--font-weight-medium)',
                        backgroundColor: 'rgba(21, 101, 192, 0.02)',
                        pointerEvents: 'none',
                        zIndex: 999,
                    }}>
                    Drop hier möglich
                </div>
            )}

            {/* Vollflächiger Drop-Bereich - unsichtbarer Overlay für bessere Drop-Erkennung */}
            {isDragActive && draggedFromStatus !== id && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 900,
                        pointerEvents: 'none',
                    }}
                />
            )}

            {children}
        </div>
    );
}

export default function TaskBoard({autoOpenTaskId, onAutoOpenComplete}: { autoOpenTaskId?: number | null, onAutoOpenComplete?: () => void } = {}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [tasks, setTasks] = useState<Task[]>([]);
    const [selectedFilter, setSelectedFilter] = useState<'assigned' | 'created' | 'due' | 'all'>(
        'assigned'
    );
    const [selectedBauvorhaben, setSelectedBauvorhaben] = useState<number | 'all'>('all');
    const [bauvorhaben, setBauvorhaben] = useState<BauvorhabenWithID[]>([]);
    const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showEditIcons, setShowEditIcons] = useState(false);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [draggedFromStatus, setDraggedFromStatus] = useState<string | null>(null);
    const {email, additionalUserdata, user_roll} = useContext(UserContext);
    const sessionContext = useSessionContext();
    const userId = (sessionContext as any).userId;

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 3,
            },
        }),
        useSensor(KeyboardSensor)
    );

    const loadTasks = async () => {
        try {
            setLoading(true);
            const allTasks = await TasksService.getTasksTaskGet();

            console.log('TaskBoard Debug Info:', {
                userId,
                sessionUserId: (sessionContext as any).userId,
                selectedFilter,
                selectedBauvorhaben,
                totalTasks: allTasks.length,
                sampleTask: allTasks[0],
            });

            let filtered;
            if (selectedFilter === 'assigned') {
                filtered = userId ? allTasks.filter((task) => task.assigned_to === userId) : [];
                console.log('Filtered assigned tasks:', filtered.length, 'for userId:', userId);
            } else if (selectedFilter === 'created') {
                filtered = userId ? allTasks.filter((task) => task.created_by === userId) : [];
                console.log('Filtered created tasks:', filtered.length, 'for userId:', userId);
            } else if (selectedFilter === 'due') {
                filtered = allTasks.filter(
                    (task) => !!task.due_date && new Date(task.due_date) <= new Date()
                );
            } else {
                filtered = allTasks;
            }

            if (selectedBauvorhaben !== 'all') {
                filtered = filtered.filter((task) => task.bauvorhaben_id === selectedBauvorhaben);
            }

            setTasks(filtered);
            setError(null);
        } catch (err) {
            console.error(err);
            setError('Aufgaben konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    const loadBauvorhaben = async () => {
        try {
            const bv = await ArbeitszeitService.getAlleBauvorhabenArbeitzeitBauvorhabenGet(false);
            setBauvorhaben(bv as unknown as BauvorhabenWithID[]);
        } catch (error) {
            console.error('Error loading Bauvorhaben:', error);
        }
    };

    useEffect(() => {
        loadBauvorhaben();
    }, []);

    // Auto-open task if specified in URL
    useEffect(() => {
        if (autoOpenTaskId && tasks.length > 0 && !selectedTaskId) {
            // Check if the task exists in our current task list
            const taskExists = tasks.some(task => task.id === autoOpenTaskId);
            if (taskExists) {
                setSelectedTaskId(autoOpenTaskId);

                // Log task opened event for auto-opened tasks (e.g., from push notifications)
                TasksService.logTaskOpenedTaskTaskIdOpenPost(autoOpenTaskId)
                    .then(() => {
                        console.log('Auto-opened task logged (Board):', autoOpenTaskId);
                    })
                    .catch((error) => {
                        console.error('Failed to log auto-opened task (Board):', error);
                    });

                if (onAutoOpenComplete) {
                    onAutoOpenComplete();
                }
            }
        }
    }, [autoOpenTaskId, tasks, selectedTaskId, onAutoOpenComplete]);

    useEffect(() => {
        loadTasks();
    }, [selectedFilter, selectedBauvorhaben, userId]);

    const updateTaskInList = (updatedTask: Task) => {
        setTasks((prevTasks) =>
            prevTasks.map((task) => (task.id === updatedTask.id ? updatedTask : task))
        );
    };

    const addTaskToList = (newTask: Task) => {
        setTasks((prevTasks) => [...prevTasks, newTask]);
    };

    const removeTaskFromList = (taskId: number) => {
        setTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));
    };

    const updateTaskStatus = async (taskId: number, newStatus: TaskStatus) => {
        try {
            const task = tasks.find((t) => t.id === taskId);
            if (!task || task.status === newStatus) return;

            if (newStatus === TaskStatus.ARCHIVED) {
                const isAdmin = user_roll?.includes('Administrator');
                const isTaskCreator = task.created_by === userId;

                if (!isAdmin && !isTaskCreator) {
                    console.error(
                        'Archivierung nicht erlaubt: Nur Ersteller oder Admin können Tasks archivieren'
                    );
                    alert('Sie können nur Tasks archivieren, die Sie selbst erstellt haben.');
                    return;
                }
            }

            const updatedTask = {...task, status: newStatus};
            updateTaskInList(updatedTask);

            await TasksService.updateTaskTaskTaskIdPut(taskId, updatedTask);

            // Push-Benachrichtigung senden wenn Status geändert wurde
            try {
                const originalStatus = task.status;

                // Benachrichtigung bei Status-Änderung zu "Abgeschlossen"
                if (newStatus === TaskStatus.COMPLETED && originalStatus !== TaskStatus.COMPLETED) {
                    console.log('🔔 Sende Push-Benachrichtigung für abgeschlossene Aufgabe...');
                    await PushNotificationHelper.sendTaskNotification(
                        'completed',
                        task.title,
                        task.assigned_to,
                        userId,
                        task.id.toString()
                    );
                }
                // Allgemeine Status-Änderungs-Benachrichtigung für zugewiesene Person
                else if (task.assigned_to && task.assigned_to !== userId) {
                    console.log('🔔 Sende Push-Benachrichtigung für Aufgaben-Update...');
                    await PushNotificationHelper.sendTaskNotification(
                        'updated',
                        task.title,
                        task.assigned_to,
                        userId,
                        task.id.toString()
                    );
                }
            } catch (error) {
                console.error('❌ Fehler beim Senden der Push-Benachrichtigung:', error);
            }
        } catch (err) {
            console.error('Fehler beim Status-Update:', err);
            const originalTask = tasks.find((t) => t.id === taskId);
            if (originalTask) {
                updateTaskInList(originalTask);
            }
        }
    };

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id as string);
        const taskId = parseInt(event.active.id as string);
        const task = tasks.find((t) => t.id === taskId);
        setDraggedFromStatus(task?.status || null);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const {active, over} = event;
        setActiveId(null);
        setDraggedFromStatus(null);

        if (!over) return;

        const taskId = parseInt(active.id as string);

        const isColumnDrop = Object.values(TaskStatus).includes(over.id as TaskStatus);

        let newStatus: TaskStatus | null = null;

        if (isColumnDrop) {
            newStatus = over.id as TaskStatus;
        } else {
            const overTask = tasks.find((t) => t.id.toString() === over.id);
            newStatus = overTask?.status || null;
        }

        if (newStatus) {
            updateTaskStatus(taskId, newStatus);
        }
    };

    const columnAwareCollisionDetection: CollisionDetection = (args) => {
        const collisions = rectIntersection(args);

        if (collisions.length === 0) return [];

        const columnCollision = collisions.find((c) =>
            Object.values(TaskStatus).includes(c.id as TaskStatus)
        );

        if (columnCollision) {
            return [columnCollision];
        }

        return collisions;
    };

    const getTasksByStatus = (status: TaskStatus) => {
        return tasks.filter((task) => task.status === status);
    };

    const handleTaskClick = (taskId: number, event: React.MouseEvent<HTMLElement>) => {
        const target = event.target as HTMLElement;
        if (!target.closest('[data-edit-button]') && !target.closest('[data-drag-handle]')) {
            setSelectedTaskId(taskId);

            // Log task opened event
            TasksService.logTaskOpenedTaskTaskIdOpenPost(taskId)
                .then(() => {
                    console.log('Task opened event logged (Board):', taskId);
                })
                .catch((error) => {
                    console.error('Failed to log task opened event (Board):', error);
                });
        }
    };

    const handleEditClick = (taskId: number, event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setSelectedTaskId(taskId);

        // Log task opened event
        TasksService.logTaskOpenedTaskTaskIdOpenPost(taskId)
            .then(() => {
                console.log('Task opened event logged (Edit):', taskId);
            })
            .catch((error) => {
                console.error('Failed to log task opened event (Edit):', error);
            });
    };

    const handleTaskUpdated = (updatedTask?: Task) => {
        if (updatedTask) {
            const shouldBeVisible = (() => {
                if (selectedFilter === 'assigned') {
                    return userId && updatedTask.assigned_to === userId;
                }
                if (selectedFilter === 'created') {
                    return userId && updatedTask.created_by === userId;
                }
                if (selectedFilter === 'due') {
                    return !!updatedTask.due_date && new Date(updatedTask.due_date) <= new Date();
                }
                return true;
            })();

            if (shouldBeVisible) {
                updateTaskInList(updatedTask);
            } else {
                removeTaskFromList(updatedTask.id);
            }
        }
        loadTasks();
        setSelectedTaskId(null);
    };

    const handleTaskDetailClose = () => {
        setSelectedTaskId(null);
    };

    const truncateText = (text: string, maxLength: number) => {
        if (!text) return '';
        return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
    };

    const getBauvorhabenName = (bauvorhabenId: number | undefined | null, projektName?: string) => {
        if (!bauvorhabenId) return projektName || 'Kein Bauvorhaben';
        const bvh = bauvorhaben.find((b) => b.id === bauvorhabenId);
        return bvh?.bvh_name || 'Unbekanntes Bauvorhaben';
    };

    const TaskCard = ({
                          task,
                          isDragging = false,
                          onCardClick,
                      }: {
        task: Task;
        isDragging?: boolean;
        onCardClick?: (event: React.MouseEvent<HTMLElement>) => void;
    }) => {
        const getDueChipColor = () => {
            if (!task.due_date) return 'default';
            const due = new Date(task.due_date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            due.setHours(0, 0, 0, 0);

            if (due < today) return 'error';
            if (due.getTime() === today.getTime()) return 'warning';
            return 'success';
        };

        const getDueLabel = () => {
            if (!task.due_date) return 'Kein Fälligkeitsdatum';
            return `Fällig am: ${new Date(task.due_date).toLocaleDateString()}`;
        };

        return (
            <Box
                onClick={onCardClick}
                sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: 'background.paper',
                    boxShadow: isDragging ? 6 : 2,
                    borderRadius: 2,
                    opacity: isDragging ? 0.8 : 1,
                    cursor: isDragging ? 'grabbing' : 'grab',
                    borderLeft:
                        task.due_date && new Date(task.due_date) < new Date() ? '4px solid red' : undefined,
                    position: 'relative',
                    transform: isDragging ? 'rotate(3deg)' : 'none',
                    transition: isDragging ? 'none' : 'all 0.2s ease',
                    '&:hover': {
                        boxShadow: isDragging ? 6 : 3,
                        transform: isDragging ? 'rotate(3deg)' : 'translateY(-2px)',
                    },
                }}>
                {/* Drag Handle - visueller Indikator */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        color: 'grey.400',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 24,
                        height: 24,
                        borderRadius: 1,
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        pointerEvents: 'none',
                    }}>
                    <DragIndicatorIcon fontSize='small'/>
                </Box>

                {/* Edit Icon */}
                <IconButton
                    data-edit-button
                    size='small'
                    onClick={(event: React.MouseEvent<HTMLElement>) => handleEditClick(task.id, event)}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: showEditIcons ? 'rgba(25, 118, 210, 0.1)' : 'rgba(255, 255, 255, 0.9)',
                        opacity: showEditIcons ? 1 : 0.7,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.2)',
                            opacity: 1,
                            transform: 'scale(1.1)',
                        },
                    }}>
                    <EditIcon fontSize='small' color={showEditIcons ? 'primary' : 'inherit'}/>
                </IconButton>

                {/* Task Content */}
                <Box sx={{pl: 4, pr: 4}}>
                    <Tooltip title={task.title} placement='top'>
                        <Typography
                            variant='subtitle1'
                            sx={{
                                mb: 0.5,
                                fontWeight: 'medium',
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                            {truncateText(task.title || '', 25)}
                        </Typography>
                    </Tooltip>

                    <Tooltip title={task.description || 'Keine Beschreibung'} placement='top'>
                        <Typography
                            variant='body2'
                            color='text.secondary'
                            sx={{
                                mb: 1,
                                lineHeight: 1.3,
                                height: '2.6em',
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                            }}>
                            {truncateText(task.description || '', 40)}
                        </Typography>
                    </Tooltip>

                    <Chip
                        size='small'
                        label={getDueLabel()}
                        color={getDueChipColor()}
                        variant={task.due_date ? 'filled' : 'outlined'}
                        sx={{
                            fontSize: '0.75rem',
                            height: 24,
                        }}
                    />
                </Box>
            </Box>
        );
    };

    const SortableTaskCard = ({task}: { task: Task }) => {
        const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
            id: task.id.toString(),
        });

        const style: React.CSSProperties = {
            transform: transform ? CSS.Transform.toString(transform) : undefined,
            transition: isDragging ? 'none' : transition,
            zIndex: isDragging ? 10000 : 'auto',
        };

        const handleCardClick = (event: React.MouseEvent<HTMLElement>) => {
            const target = event.target as HTMLElement;
            if (target.closest('[data-edit-button]')) {
                return;
            }
            handleTaskClick(task.id, event);
        };

        return (
            <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
                <TaskCard task={task} isDragging={isDragging} onCardClick={handleCardClick}/>
            </div>
        );
    };

    const statusColumns: { icon: React.ReactNode; label: string; status: TaskStatus }[] = [
        {icon: <FormatListBullet/>, label: 'Offen', status: TaskStatus.OPEN},
        {icon: <RotateRightIcon/>, label: 'In Bearbeitung', status: TaskStatus.IN_PROGRESS},
        {icon: <BlockIcon/>, label: 'Blockiert', status: TaskStatus.BLOCKED},
        {icon: <DoneAllIcon/>, label: 'Abgeschlossen', status: TaskStatus.COMPLETED},
    ];

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                <CircularProgress/>
            </Box>
        );
    }

    if (error) {
        return <Alert severity='error'>{error}</Alert>;
    }

    const activeTask = activeId ? tasks.find((task) => task.id.toString() === activeId) : null;

    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundColor: 'var(--background-default)',
            }}>
            {/* Header mit Filter-Dropdowns */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 4,
                    p: 3,
                    backgroundColor: 'var(--background-paper)',
                    borderRadius: 'var(--border-radius)',
                    boxShadow: 'var(--shadow-1)',
                    border: '1px solid var(--divider)',
                    flexWrap: 'wrap',
                    gap: 2,
                }}>
                <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center'}}>
                    <FormControl variant='outlined' size='medium' sx={{minWidth: 220}}>
                        <InputLabel id='task-filter-label'>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <FilterListIcon fontSize='small'/>
                                Aufgaben filtern
                            </Box>
                        </InputLabel>
                        <Select
                            labelId='task-filter-label'
                            value={selectedFilter}
                            onChange={(e) =>
                                setSelectedFilter(e.target.value as 'assigned' | 'created' | 'due' | 'all')
                            }
                            label='Aufgaben filtern'>
                            <MenuItem value='assigned'>
                                <AssignmentIcon fontSize='small' sx={{marginRight: 1.5}}/>
                                Mir zugewiesene Aufgaben
                            </MenuItem>
                            <MenuItem value='created'>
                                <CreateIcon fontSize='small' sx={{marginRight: 1.5}}/>
                                Von mir erstellte Aufgaben
                            </MenuItem>
                            <MenuItem value='due'>
                                <ScheduleIcon fontSize='small' sx={{marginRight: 1.5}}/>
                                Heute fällige Aufgaben
                            </MenuItem>
                            <MenuItem value='all'>
                                <ViewListIcon fontSize='small' sx={{marginRight: 1.5}}/>
                                Alle Aufgaben anzeigen
                            </MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl variant='outlined' size='medium' sx={{minWidth: 200}}>
                        <InputLabel id='bauvorhaben-filter-label'>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <BusinessIcon fontSize='small'/>
                                Bauvorhaben
                            </Box>
                        </InputLabel>
                        <Select
                            labelId='bauvorhaben-filter-label'
                            value={selectedBauvorhaben}
                            onChange={(e) => setSelectedBauvorhaben(e.target.value as number | 'all')}
                            label='Bauvorhaben'>
                            <MenuItem value='all'>
                                <ViewListIcon fontSize='small' sx={{marginRight: 1.5}}/>
                                Alle Bauvorhaben
                            </MenuItem>
                            {bauvorhaben.map((bvh) => (
                                <MenuItem key={bvh.id} value={bvh.id}>
                                    <BusinessIcon fontSize='small' sx={{marginRight: 1.5}}/>
                                    {bvh.bvh_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Typography variant='body2' color='text.secondary'>
                    {tasks.length} Aufgabe{tasks.length !== 1 ? 'n' : ''} gefunden
                </Typography>
            </Box>

            <Box
                sx={{
                    backgroundColor: 'var(--background-paper)',
                    borderRadius: 'var(--border-radius)',
                    p: 3,
                    boxShadow: 'var(--shadow-1)',
                    border: '1px solid var(--divider)',
                    minHeight: 'auto',
                    overflow: 'visible',
                }}>
                <DndContext
                    sensors={sensors}
                    collisionDetection={columnAwareCollisionDetection}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}>
                    <Grid
                        container
                        spacing={3}
                        sx={{minHeight: isMobile ? '500px' : '700px', padding: '8px'}}>
                        {statusColumns.map((col, index) => {
                            const taskCount = getTasksByStatus(col.status).length;

                            return (
                                <Grid item xs={12} md={3} key={col.status}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mb: 2,
                                            p: 2,
                                            backgroundColor: 'var(--background-level1)',
                                            borderRadius: 'var(--border-radius)',
                                            border: '1px solid var(--divider)',
                                            transition: 'all var(--transition-normal)',
                                            '&:hover': {
                                                borderColor: 'var(--primary-main)',
                                                backgroundColor: 'var(--background-paper)',
                                            },
                                        }}>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                            <Box
                                                sx={{
                                                    color: 'var(--primary-main)',
                                                    fontSize: '1.1rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}>
                                                {col.icon}
                                            </Box>
                                            <Typography
                                                variant='subtitle1'
                                                sx={{
                                                    color: 'var(--text-primary)',
                                                    fontWeight: 'var(--font-weight-medium)',
                                                    fontSize: '1rem',
                                                    mb: 0,
                                                }}>
                                                {col.label}
                                            </Typography>
                                        </Box>

                                        <Chip
                                            label={taskCount}
                                            size='small'
                                            variant='outlined'
                                            sx={{
                                                borderColor: 'var(--primary-main)',
                                                color: 'var(--primary-main)',
                                                fontWeight: 'var(--font-weight-medium)',
                                                minWidth: '28px',
                                                height: '24px',
                                                fontSize: '0.75rem',
                                                '& .MuiChip-label': {
                                                    px: 1,
                                                },
                                            }}
                                        />
                                    </Box>

                                    {/* Droppable Column */}
                                    <DroppableColumn
                                        id={col.status}
                                        isMobile={isMobile}
                                        isDragActive={!!activeId}
                                        draggedFromStatus={draggedFromStatus}>
                                        <SortableContext
                                            items={getTasksByStatus(col.status).map((t) => t.id.toString())}
                                            strategy={verticalListSortingStrategy}>
                                            {getTasksByStatus(col.status).map((task) => (
                                                <SortableTaskCard key={task.id} task={task}/>
                                            ))}
                                        </SortableContext>

                                        {/* Empty state indicator */}
                                        {getTasksByStatus(col.status).length === 0 && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    minHeight: '200px',
                                                    color: 'var(--text-secondary)',
                                                    textAlign: 'center',
                                                    gap: 1,
                                                }}>
                                                <Box sx={{fontSize: '2rem', opacity: 0.3}}>{col.icon}</Box>
                                                <Typography
                                                    variant='body2'
                                                    sx={{
                                                        opacity: 0.6,
                                                        fontStyle: 'italic',
                                                        fontSize: '0.875rem',
                                                    }}>
                                                    Keine Aufgaben
                                                </Typography>
                                            </Box>
                                        )}
                                    </DroppableColumn>
                                </Grid>
                            );
                        })}
                    </Grid>

                    {/* Drag Overlay für bessere Performance */}
                    <DragOverlay>
                        {activeTask ? <TaskCard task={activeTask} isDragging={true}/> : null}
                    </DragOverlay>
                </DndContext>
            </Box>

            {selectedTaskId && (
                <TaskDetail
                    taskId={selectedTaskId}
                    isOpen={true}
                    onClose={handleTaskDetailClose}
                    onTaskUpdated={handleTaskUpdated}
                />
            )}
        </Box>
    );
}
